/**
 * ログイン画面。ログインは社内ID(2026-08-26)。
 * パスワードを忘れたときは、この画面から承認者へ再設定を依頼できる(2026-09-03 指示)。
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { useAuth } from './AuthProvider';
import { loginIdToEmail, normalizeLoginId } from '@core/lib/loginId';
import { normalizePassword } from '@core/lib/password';
import { createResetRequest } from '@core/masters/secretsRepo';

function messageFor(e: unknown): string {
  if (e instanceof FirebaseError) {
    switch (e.code) {
      case 'auth/invalid-email':
        return 'IDの形式が正しくありません。';
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'IDまたはパスワードが違います。';
      case 'auth/user-disabled':
        return 'このアカウントは無効化されています。管理者に連絡してください。';
      case 'auth/too-many-requests':
        return '試行回数が多すぎます。しばらく待ってからお試しください。';
      default:
        return `ログインできませんでした(${e.code})`;
    }
  }
  return 'ログインできませんでした。通信状況を確認してください。';
}

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  /** パスワードを忘れたときの依頼(2026-09-03) */
  const [forgot, setForgot] = useState(false);
  const [forgotNote, setForgotNote] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      // ログインは社内IDのみ(2026-08-26)。メールアドレス入力は受け付けない。
      // スペースが混ざっていても入れるようにする(2026-09-10 指示)
      const id = normalizeLoginId(email);
      if (id.includes('@')) {
        setError('社内IDを入力してください(メールアドレスでのログインはできません)。');
        setBusy(false);
        return;
      }
      await signIn(loginIdToEmail(id), normalizePassword(password));
      // ログイン後は必ず経費申請から始める(2026-08-31 指示)。
      // 前回開いていた画面(支払い・承認など)のURLが残っていても経費申請に戻す
      navigate('/apply', { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <img className="login-logo" src="/brand/logo.png?v=5" alt="Nexus Gate" />
        <div className="brand">SMART CONTROL / SMART CFO</div>
        <h1 style={{ marginBottom: 4 }}>Nexus Gate</h1>
        <div className="login-tagline" style={{ marginBottom: 20 }}>経営と財務を、ひと目で。</div>

        <label htmlFor="email">社内ID</label>
        <input
          id="email"
          type="text"
          autoComplete="username"
          placeholder="社内ID"
          value={email}
          onChange={(e) => setEmail(normalizeLoginId(e.target.value))}
          required
        />

        <label htmlFor="password">パスワード</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(normalizePassword(e.target.value))}
          required
        />

        <button className="btn-primary" type="submit" disabled={busy}>
          {busy ? 'ログイン中…' : 'ログイン'}
        </button>

        {error && <div className="login-error">{error}</div>}

        {/* パスワードを忘れたとき(2026-09-03 指示)。承認者に再設定を依頼する */}
        {!forgot ? (
          <button
            type="button"
            className="login-link"
            onClick={() => {
              setForgot(true);
              setError('');
            }}
          >
            パスワードをお忘れの方
          </button>
        ) : forgotSent ? (
          <div className="login-forgot">
            <b>依頼を送りました。</b>
            <br />
            承認者が新しいパスワードを発行してお伝えします。しばらくお待ちください。
          </div>
        ) : (
          <div className="login-forgot">
            <b>パスワードの再設定を依頼します</b>
            <br />
            上の「社内ID」を入力してから、依頼を送ってください。承認者が新しいパスワードを発行します。
            <input
              type="text"
              placeholder="連絡先(内線・携帯など。任意)"
              value={forgotNote}
              onChange={(e) => setForgotNote(e.target.value)}
              style={{ marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={() => {
                  const id = email.trim().toLowerCase();
                  if (!id || id.includes('@')) {
                    setError('社内IDを入力してから、依頼を送ってください。');
                    return;
                  }
                  setBusy(true);
                  void createResetRequest(id, forgotNote.slice(0, 200))
                    .then(() => setForgotSent(true))
                    .catch(() => setError('依頼を送れませんでした。通信状況を確認してください。'))
                    .finally(() => setBusy(false));
                }}
              >
                依頼を送る
              </button>
              <button type="button" className="btn-line" onClick={() => setForgot(false)}>
                やめる
              </button>
            </div>
          </div>
        )}

        <div className="login-note">build {__BUILD_ID__}{__COMMIT_ID__ && ` · ${__COMMIT_ID__}`}</div>
      </form>
    </div>
  );
}
